import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

// VÉRAULT Design System - Day & Night Color Palettes
// Night Mode: Calm, refined, low-light luxury experience (like a quiet hotel lobby at night)
export const THEME_COLORS = {
  day: {
    // Backgrounds
    bg: '#F5F1EB',           // Bone - primary background
    bgCard: '#FFFFFF',       // White - card backgrounds
    bgMuted: '#E6E4DE',      // Light stone - muted backgrounds
    bgAccent: '#F9F8F4',     // Off-white - subtle accent
    bgElevated: '#FFFFFF',   // Elevated surfaces
    
    // Text
    text: '#1A1A1A',         // Charcoal - primary text
    textMuted: '#8C8C8C',    // Gray - secondary text
    textSubtle: '#595959',   // Dark gray - subtle text
    
    // Accents
    gold: '#C8A85A',         // Micro-gold - primary accent
    goldMuted: 'rgba(200, 168, 90, 0.15)',
    terra: '#B37E6B',        // Terra - alerts/warnings
    terraMuted: 'rgba(179, 126, 107, 0.15)',
    sage: '#8DA399',         // Sage - success
    sageMuted: 'rgba(141, 163, 153, 0.15)',
    stone: '#8C8A86',        // Stone - neutral accent
    
    // Borders
    border: 'rgba(0, 0, 0, 0.06)',
    borderStrong: 'rgba(0, 0, 0, 0.12)',
    
    // Status banner
    statusBg: '#1A1A1A',
    statusText: '#FFFFFF',
    
    // Inputs
    inputBg: '#F9F8F4',
    inputBorder: 'rgba(0, 0, 0, 0.08)',
  },
  night: {
    // ==================== LOCKED NIGHT MODE PALETTE ====================
    // Final Color Consolidation - SINGLE SOURCE OF TRUTH
    // DO NOT MODIFY WITHOUT EXPLICIT APPROVAL
    
    // Dark Backgrounds
    bg: '#121716',           // Primary Night Background
    bgCard: '#171C1B',       // Secondary Night Surface
    bgMuted: '#121716',      // Same as bg
    bgAccent: '#171C1B',     // Secondary surface
    bgElevated: '#171C1B',   // Elevated surfaces
    
    // PRIMARY WARM CREAM - MANDATORY SINGLE VALUE
    bgCream: '#EFE6D8',      // THE ONLY cream color allowed
    
    // Text on Dark Backgrounds
    text: '#F2F2F0',         // Primary Light Text
    textMuted: '#B7B0A4',    // Muted Text
    textSubtle: '#B7B0A4',   // Same as muted
    
    // Text on Cream Backgrounds
    textOnCream: '#2A2A2A',  // Dark charcoal on cream
    textMutedOnCream: '#5A5A54', // Muted on cream
    
    // Accents - Gold
    gold: '#C9A24D',         // Gold Accent
    goldMuted: 'rgba(201, 162, 77, 0.25)',
    terra: '#D4A090',        // Softer terra for night
    terraMuted: 'rgba(212, 160, 144, 0.20)',
    sage: '#8DA399',         // Sage for success
    sageMuted: 'rgba(141, 163, 153, 0.20)',
    stone: '#B7B0A4',        // Stone matches muted text
    
    // Borders & Dividers
    border: '#2A302E',       // Hairline divider
    borderStrong: '#3A403E', // Stronger border
    
    // Status banner - Uses warm cream
    statusBg: '#EFE6D8',
    statusText: '#2A2A2A',
    
    // Inputs
    inputBg: '#171C1B',
    inputBorder: '#2A302E',
  }
};

export function ThemeProvider({ children }) {
  const [themeMode, setThemeMode] = useState('day'); // 'auto', 'day', 'night'
  const [temperatureUnit, setTemperatureUnit] = useState('fahrenheit');
  const [currentTheme, setCurrentTheme] = useState('day');

  // Load preferences from localStorage
  useEffect(() => {
    const savedThemeMode = localStorage.getItem('verault_theme_mode');
    const savedTempUnit = localStorage.getItem('verault_temp_unit');
    
    if (savedThemeMode) setThemeMode(savedThemeMode);
    if (savedTempUnit) setTemperatureUnit(savedTempUnit);
  }, []);

  // Calculate current theme based on mode
  useEffect(() => {
    const calculateTheme = () => {
      if (themeMode === 'day') return 'day';
      if (themeMode === 'night') return 'night';
      
      // Auto mode: based on system time
      const hour = new Date().getHours();
      if (hour >= 20 || hour < 6) return 'night';
      return 'day';
    };

    setCurrentTheme(calculateTheme());

    if (themeMode === 'auto') {
      const interval = setInterval(() => {
        setCurrentTheme(calculateTheme());
      }, 60000);
      return () => clearInterval(interval);
    }
  }, [themeMode]);

  // Apply theme to document
  useEffect(() => {
    const colors = THEME_COLORS[currentTheme];
    const root = document.documentElement;
    
    // Set CSS variables for global use
    root.style.setProperty('--vt-bg', colors.bg);
    root.style.setProperty('--vt-bg-card', colors.bgCard);
    root.style.setProperty('--vt-bg-muted', colors.bgMuted);
    root.style.setProperty('--vt-bg-accent', colors.bgAccent);
    root.style.setProperty('--vt-bg-elevated', colors.bgElevated);
    root.style.setProperty('--vt-text', colors.text);
    root.style.setProperty('--vt-text-muted', colors.textMuted);
    root.style.setProperty('--vt-text-subtle', colors.textSubtle);
    root.style.setProperty('--vt-gold', colors.gold);
    root.style.setProperty('--vt-gold-muted', colors.goldMuted);
    root.style.setProperty('--vt-terra', colors.terra);
    root.style.setProperty('--vt-terra-muted', colors.terraMuted);
    root.style.setProperty('--vt-sage', colors.sage);
    root.style.setProperty('--vt-sage-muted', colors.sageMuted);
    root.style.setProperty('--vt-stone', colors.stone);
    root.style.setProperty('--vt-border', colors.border);
    root.style.setProperty('--vt-border-strong', colors.borderStrong);
    root.style.setProperty('--vt-status-bg', colors.statusBg);
    root.style.setProperty('--vt-status-text', colors.statusText);
    root.style.setProperty('--vt-input-bg', colors.inputBg);
    root.style.setProperty('--vt-input-border', colors.inputBorder);
    
    // Set body class for potential CSS overrides
    document.body.classList.remove('theme-day', 'theme-night');
    document.body.classList.add(`theme-${currentTheme}`);
  }, [currentTheme]);

  const updateThemeMode = (mode) => {
    setThemeMode(mode);
    localStorage.setItem('verault_theme_mode', mode);
  };

  const updateTemperatureUnit = (unit) => {
    setTemperatureUnit(unit);
    localStorage.setItem('verault_temp_unit', unit);
  };

  const formatTemperature = (tempCelsius) => {
    if (!tempCelsius && tempCelsius !== 0) return null;
    
    const numTemp = typeof tempCelsius === 'string' 
      ? parseFloat(tempCelsius.replace(/[^\d.-]/g, '')) 
      : tempCelsius;
    
    if (isNaN(numTemp)) return tempCelsius;
    
    if (temperatureUnit === 'fahrenheit') {
      const fahrenheit = Math.round((numTemp * 9/5) + 32);
      return `${fahrenheit}°F`;
    }
    return `${Math.round(numTemp)}°C`;
  };

  // Get current color palette
  const colors = THEME_COLORS[currentTheme];

  const value = {
    themeMode,
    currentTheme,
    temperatureUnit,
    colors,
    setThemeMode: updateThemeMode,
    setTemperatureUnit: updateTemperatureUnit,
    formatTemperature,
    isNightMode: currentTheme === 'night',
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

export default ThemeContext;
